import { Dialog } from "@headlessui/react";
import {
  CheckIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import clsx from "clsx";

const variantStyles = {
  success: { Icon: CheckIcon, bg: "bg-green-50", text: "text-green-500" },
  warning: {
    Icon: ExclamationTriangleIcon,
    bg: "bg-orange-50",
    text: "text-orange-500",
  },
  error: { Icon: XMarkIcon, bg: "bg-red-50", text: "text-red-500" },
};

interface StatusModalProps {
  isOpen: boolean;
  variant: keyof typeof variantStyles;
  title: string;
  message: string;
  buttonLabel?: string;
  onClose: () => void;
}

const StatusModal = ({
  isOpen,
  variant,
  title,
  message,
  buttonLabel = "Done",
  onClose,
}: StatusModalProps) => {
  if (!isOpen) return null;

  const { Icon, bg, text } = variantStyles[variant];

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div
        className="fixed inset-0 bg-black/45 backdrop-blur-sm"
        aria-hidden="true"
      />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="w-full max-w-150 rounded-2xl bg-white md:p-8 p-5 shadow-xl text-center">
          <div className="flex flex-col items-center">
            <div
              className={clsx(
                "flex size-14 items-center justify-center rounded-full",
                bg,
              )}
            >
              <Icon className={clsx("size-7", text)} />
            </div>
            <Dialog.Title className="mt-4 md:text-2xl text-xl font-semibold text-gray-950">
              {title}
            </Dialog.Title>
          </div>

          <p className="mt-2 text-sm md:text-base font-normal text-gray-500">
            {message}
          </p>

          <button
            type="button"
            onClick={onClose}
            className="mt-8 h-13 w-full rounded-xl text-base font-medium text-white bg-blue-600 hover:bg-blue-700 cursor-pointer transition-colors"
          >
            {buttonLabel}
          </button>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default StatusModal;
